import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Building2, Trash2, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../../components/ci/ciApi";
import { RagBadge, Panel, Kicker, EmptyNote } from "../../components/ci/ciWidgets";

const STATUS_STYLES = {
  draft: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  analyzing: "bg-teal-500/15 text-teal-300 border-teal-400/30",
  complete: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  error: "bg-red-500/15 text-red-300 border-red-400/30",
};

export default function CIDashboard() {
  const [stats, setStats] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [s, a] = await Promise.all([ciApi.get("/stats"), ciApi.get("/assessments")]);
      setStats(s.data);
      setAssessments(a.data.assessments);
    } catch (e) {
      toast.error("Failed to load the dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete the assessment for ${name}? This removes its sources and report.`)) return;
    await ciApi.delete(`/assessments/${id}`);
    toast.success("Assessment deleted");
    load();
  };

  const cards = stats
    ? [
        ["Companies assessed", stats.companies_assessed],
        ["Average value score", stats.average_score ?? "—"],
        ["High-risk companies", stats.high_risk],
        ["Value opportunities", stats.value_opportunities],
        ["In progress", stats.in_progress],
        ["Avg evidence coverage", stats.avg_evidence_coverage != null ? `${stats.avg_evidence_coverage}%` : "—"],
      ]
    : [];

  return (
    <div data-testid="ci-dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <Kicker className="mb-3">Intelligence Suite · Dashboard</Kicker>
            <h1 className="font-serif text-3xl sm:text-4xl">Company assessments</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="p-2.5 rounded-lg border border-white/12 text-white/50 hover:text-white hover:border-white/30 transition-colors" title="Refresh" data-testid="ci-dashboard-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link
              to="/company-intelligence/new"
              data-testid="ci-dashboard-new-btn"
              className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 text-[#06251f] font-bold text-sm px-6 py-2.5 transition-colors"
            >
              <PlusCircle className="w-4 h-4" /> New assessment
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
              {cards.map(([label, value]) => (
                <Panel key={label} className="px-5 py-5">
                  <div className="font-serif text-3xl text-white tabular-nums mb-1">{value}</div>
                  <div className="text-[10px] tracking-[0.16em] uppercase text-white/45 font-semibold">{label}</div>
                </Panel>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <Panel className="lg:col-span-2 overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 text-[11px] tracking-[0.2em] uppercase font-bold text-white/55">
                  Assessments
                </div>
                {assessments.length === 0 ? (
                  <EmptyNote>No assessments yet. Create your first company assessment to begin.</EmptyNote>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] tracking-[0.14em] uppercase text-white/40 border-b border-white/10">
                        <th className="px-6 py-3 font-semibold">Company</th>
                        <th className="px-3 py-3 font-semibold">Industry</th>
                        <th className="px-3 py-3 font-semibold">Status</th>
                        <th className="px-3 py-3 font-semibold">Score</th>
                        <th className="px-3 py-3 font-semibold">Evidence</th>
                        <th className="px-3 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {assessments.map((a) => (
                        <tr
                          key={a.id}
                          data-testid={`ci-assessment-row-${a.id}`}
                          className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                          onClick={() => navigate(`/company-intelligence/workspace/${a.id}`)}
                        >
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-3">
                              <Building2 className="w-4 h-4 text-teal-300/70 shrink-0" />
                              <span className="font-medium text-white">{a.company?.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3.5 text-white/55">{a.company?.industry || "—"}</td>
                          <td className="px-3 py-3.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-[0.12em] uppercase ${STATUS_STYLES[a.status] || STATUS_STYLES.draft}`}>
                              {a.status}
                            </span>
                          </td>
                          <td className="px-3 py-3.5">
                            {a.overall_score != null ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="font-serif text-lg tabular-nums">{a.overall_score}</span>
                                <RagBadge rag={a.overall_rag} />
                              </span>
                            ) : (
                              <span className="text-white/30">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3.5 text-white/55 tabular-nums">
                            {a.evidence_coverage != null ? `${a.evidence_coverage}%` : "—"}
                          </td>
                          <td className="px-3 py-3.5 text-right pr-6">
                            <span className="inline-flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(a.id, a.company?.name);
                                }}
                                data-testid={`ci-assessment-delete-${a.id}`}
                                className="p-1.5 rounded text-white/30 hover:text-red-300 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <ArrowRight className="w-4 h-4 text-white/30" />
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>

              <Panel>
                <div className="px-6 py-4 border-b border-white/10 text-[11px] tracking-[0.2em] uppercase font-bold text-white/55">
                  Industry distribution
                </div>
                <div className="p-6 space-y-3">
                  {(stats?.industry_distribution || []).length === 0 ? (
                    <EmptyNote>No data yet</EmptyNote>
                  ) : (
                    stats.industry_distribution.slice(0, 8).map((row) => {
                      const max = stats.industry_distribution[0].count;
                      return (
                        <div key={row.industry}>
                          <div className="flex justify-between text-[12px] mb-1">
                            <span className="text-white/70">{row.industry}</span>
                            <span className="text-white/40 tabular-nums">{row.count}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                            <div className="h-full bg-teal-400/70 rounded-full" style={{ width: `${(row.count / max) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Panel>
            </div>
          </>
        )}
      </div>
  );
}
